/**
 * Money input helpers — currency is always at most 2 decimal places (pence).
 *
 * Flow: `sanitizeMoneyInput` on `onChange` keeps the field numeric while typing,
 * then `formatMoney2` on `onBlur` rounds to exactly 2dp (4.567 → "4.57",
 * 1200.5 → "1200.50"). `parseMoney` rounds to 2dp for storage/submit so
 * floating-point noise never reaches the API.
 */

/** Keep only digits and a single decimal point while the user types. */
export function sanitizeMoneyInput(raw: string): string {
  let v = raw.replace(/[^\d.]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot !== -1) {
    // Collapse any further dots after the first.
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
  }
  return v;
}

/** Round a number to 2 decimal places (pence). */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Parse a money string and round to 2dp; returns 0 for empty/invalid. */
export function parseMoney(raw: string): number {
  return round2(parseFloat(raw) || 0);
}

/**
 * Blur formatter: normalise to exactly 2 decimals. Empty stays empty so the
 * placeholder shows; otherwise "4.567" → "4.57", "4.6" → "4.60".
 */
export function formatMoney2(raw: string): string {
  if (!raw.trim()) return '';
  const n = parseFloat(raw);
  if (isNaN(n)) return '';
  return round2(n).toFixed(2);
}
